import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import {createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState} from "react";

interface ProfileNavigationContextValue {
    federated: boolean;
    startHostNavigation?: (profileUrl: string) => void;
}

const standaloneNavigationValue: ProfileNavigationContextValue = {federated: false};
const federatedNavigationValue: ProfileNavigationContextValue = {federated: true};
const ProfileNavigationContext = createContext(standaloneNavigationValue);

export function FederatedNavigationProvider({children}: PropsWithChildren) {
    return (
        <ProfileNavigationContext.Provider value={federatedNavigationValue}>
            {children}
        </ProfileNavigationContext.Provider>
    );
}

export function StandaloneNavigationProvider({children}: PropsWithChildren) {
    const [pendingProfileUrl, setPendingProfileUrl] = useState<string>();
    const startHostNavigation = useCallback((profileUrl: string) => {
        setPendingProfileUrl((currentUrl) => currentUrl ?? profileUrl);
    }, []);
    const value = useMemo(() => ({federated: false, startHostNavigation}), [startHostNavigation]);

    useEffect(() => {
        if (!pendingProfileUrl) return;

        let navigationFrame = 0;
        const loadingFrame = window.requestAnimationFrame(() => {
            navigationFrame = window.requestAnimationFrame(() => {
                window.location.assign(pendingProfileUrl);
            });
        });

        return () => {
            window.cancelAnimationFrame(loadingFrame);
            window.cancelAnimationFrame(navigationFrame);
        };
    }, [pendingProfileUrl]);

    return (
        <ProfileNavigationContext.Provider value={value}>
            {children}
            {pendingProfileUrl ? (
                <Box
                    aria-label="Loading profile"
                    role="status"
                    sx={{
                        alignItems: "center",
                        backgroundColor: "background.default",
                        display: "flex",
                        inset: 0,
                        justifyContent: "center",
                        position: "fixed",
                        zIndex: "modal",
                    }}
                >
                    <CircularProgress aria-hidden={true} />
                </Box>
            ) : null}
        </ProfileNavigationContext.Provider>
    );
}

export function useFederatedNavigation() {
    return useContext(ProfileNavigationContext).federated;
}

export function useProfileNavigation() {
    return useContext(ProfileNavigationContext);
}
